// Copyright 2026, Auxilio Technologies LLC

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { routesAPI } from '../services/api';
import {
  MapIcon,
  TruckIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  AdjustmentsHorizontalIcon,
  PlayIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline';
import { MapContainer, TileLayer, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet with webpack
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// US city coordinates for route mapping
const cityCoordinates: { [key: string]: [number, number] } = {
  'DC-Chicago': [41.8781, -87.6298],
  'Milwaukee': [43.0389, -87.9065],
  'Madison': [43.0731, -89.4012],
  'Minneapolis': [44.9778, -93.2650],
  'Des Moines': [41.5868, -93.6250],
  'Omaha': [41.2565, -95.9345],
  'Kansas City': [39.0997, -94.5786],
  'St. Louis': [38.6270, -90.1994],
  'Indianapolis': [39.7684, -86.1581],
  'DC-Newark': [40.7357, -74.1724],
  'DC-Atlanta': [33.7490, -84.3880],
  'Philadelphia': [39.9526, -75.1652],
  'Baltimore': [39.2904, -76.6122],
  'Washington DC': [38.9072, -77.0369],
  'Richmond': [37.5407, -77.4360],
  'Charlotte': [35.2271, -80.8431],
  'Port-Seattle': [47.6062, -122.3321],
  'DC-Sacramento': [38.5816, -121.4944],
  'Portland': [45.5152, -122.6784],
  'Eugene': [44.0521, -123.0868],
  'Medford': [42.3265, -122.8756],
  'Redding': [40.5865, -122.3917],
  'WH-Dallas': [32.7767, -96.7970],
  'Fort Worth': [32.7555, -97.3308],
  'Austin': [30.2672, -97.7431],
  'San Antonio': [29.4241, -98.4936],
  'Houston': [29.7604, -95.3698],
  'Beaumont': [30.0802, -94.1266],
  'Tyler': [32.3513, -95.3011],
  'DC-Detroit': [42.3314, -83.0458],
  'Toledo': [41.6528, -83.5379],
  'Cleveland': [41.4993, -81.6944],
  'Erie': [42.1292, -80.0851],
  'Buffalo': [42.8864, -78.8784],
  'Rochester': [43.1566, -77.6088],
  'Syracuse': [43.0481, -76.1474],
  'Grand Rapids': [42.9634, -85.6681],
  'Store-Miami-12': [25.7617, -80.1918],
  'Macon': [32.8407, -83.6324],
  'Jacksonville': [30.3322, -81.6557],
  'Orlando': [28.5383, -81.3792],
  'West Palm Beach': [26.7153, -80.0534],
};

interface Route {
  id: string;
  name: string;
  origin: string;
  destination: string;
  stops: number;
  distance: number;
  duration: string;
  estimatedCost: number;
  fuelConsumption: number;
  co2Emissions: number;
  status: 'optimized' | 'needs_optimization' | 'in_progress' | 'scheduled';
  efficiency: number;
  vehicleType: string;
  driver?: string;
  scheduledDate: string;
  waypoints: string[];
}

interface OptimizationResult {
  originalDistance: number;
  optimizedDistance: number;
  distanceSaved: number;
  originalDuration: string;
  optimizedDuration: string;
  timeSaved: string;
  costSavings: number;
  fuelSaved: number;
  co2Reduced: number;
}

const mockRoutes: Route[] = [
  {
    id: 'RT-001',
    name: 'Midwest Distribution Loop',
    origin: 'DC-Chicago',
    destination: 'DC-Chicago',
    stops: 8,
    distance: 1245,
    duration: '18h 30m',
    estimatedCost: 2850,
    fuelConsumption: 185,
    co2Emissions: 425,
    status: 'optimized',
    efficiency: 94,
    vehicleType: 'Semi-Truck (53ft)',
    driver: 'Mike Johnson',
    scheduledDate: '2024-01-11',
    waypoints: ['Milwaukee', 'Madison', 'Minneapolis', 'Des Moines', 'Omaha', 'Kansas City', 'St. Louis', 'Indianapolis'],
  },
  {
    id: 'RT-002',
    name: 'East Coast Express',
    origin: 'DC-Newark',
    destination: 'DC-Atlanta',
    stops: 5,
    distance: 892,
    duration: '14h 15m',
    estimatedCost: 2100,
    fuelConsumption: 132,
    co2Emissions: 303,
    status: 'needs_optimization',
    efficiency: 76,
    vehicleType: 'Semi-Truck (53ft)',
    driver: 'Sarah Williams',
    scheduledDate: '2024-01-11',
    waypoints: ['Philadelphia', 'Baltimore', 'Washington DC', 'Richmond', 'Charlotte'],
  },
  {
    id: 'RT-003',
    name: 'Pacific Northwest Run',
    origin: 'Port-Seattle',
    destination: 'DC-Sacramento',
    stops: 4,
    distance: 756,
    duration: '12h 45m',
    estimatedCost: 1780,
    fuelConsumption: 112,
    co2Emissions: 258,
    status: 'in_progress',
    efficiency: 88,
    vehicleType: 'Semi-Truck (48ft)',
    driver: 'Tom Anderson',
    scheduledDate: '2024-01-10',
    waypoints: ['Portland', 'Eugene', 'Medford', 'Redding'],
  },
  {
    id: 'RT-004',
    name: 'Texas Triangle',
    origin: 'WH-Dallas',
    destination: 'WH-Dallas',
    stops: 6,
    distance: 1120,
    duration: '16h 00m',
    estimatedCost: 2450,
    fuelConsumption: 166,
    co2Emissions: 382,
    status: 'scheduled',
    efficiency: 82,
    vehicleType: 'Semi-Truck (53ft)',
    scheduledDate: '2024-01-12',
    waypoints: ['Fort Worth', 'Austin', 'San Antonio', 'Houston', 'Beaumont', 'Tyler'],
  },
  {
    id: 'RT-005',
    name: 'Great Lakes Circuit',
    origin: 'DC-Detroit',
    destination: 'DC-Detroit',
    stops: 7,
    distance: 980,
    duration: '15h 20m',
    estimatedCost: 2280,
    fuelConsumption: 145,
    co2Emissions: 334,
    status: 'needs_optimization',
    efficiency: 71,
    vehicleType: 'Box Truck (26ft)',
    driver: 'James Brown',
    scheduledDate: '2024-01-11',
    waypoints: ['Toledo', 'Cleveland', 'Erie', 'Buffalo', 'Rochester', 'Syracuse', 'Grand Rapids'],
  },
  {
    id: 'RT-006',
    name: 'Southeast Delivery',
    origin: 'DC-Atlanta',
    destination: 'Store-Miami-12',
    stops: 4,
    distance: 662,
    duration: '10h 30m',
    estimatedCost: 1520,
    fuelConsumption: 98,
    co2Emissions: 225,
    status: 'optimized',
    efficiency: 92,
    vehicleType: 'Semi-Truck (48ft)',
    driver: 'Lisa Garcia',
    scheduledDate: '2024-01-10',
    waypoints: ['Macon', 'Jacksonville', 'Orlando', 'West Palm Beach'],
  },
];

const mockOptimizationResult: OptimizationResult = {
  originalDistance: 892,
  optimizedDistance: 756,
  distanceSaved: 136,
  originalDuration: '14h 15m',
  optimizedDuration: '11h 45m',
  timeSaved: '2h 30m',
  costSavings: 420,
  fuelSaved: 24,
  co2Reduced: 55,
};

const RouteOptimization: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const cameFromAlerts = useRef(false);
  const [routes, setRoutes] = useState<Route[]>(mockRoutes);
  const [selectedRoute, setSelectedRoute] = useState<Route | null>(null);
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [optimizationResult, setOptimizationResult] = useState<OptimizationResult | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [routeDetailsRoute, setRouteDetailsRoute] = useState<Route | null>(null);
  const [advancedFilters, setAdvancedFilters] = useState({
    vehicleType: '',
    minEfficiency: 0,
    maxEfficiency: 100,
    minDistance: 0,
    maxDistance: 2000,
    driver: '',
  });

  // Fetch routes from API on mount
  useEffect(() => {
    const fetchRoutes = async () => {
      try {
        const response = await routesAPI.list();
        if (response.data?.routes && Array.isArray(response.data.routes)) {
          setRoutes(response.data.routes);
        }
      } catch (error) {
        console.error('Error fetching routes:', error);
        // Keep mock data as fallback
      }
    };
    fetchRoutes();
  }, []);

  // Get unique vehicle types and drivers for filter dropdowns
  const vehicleTypes = [...new Set(routes.map(r => r.vehicleType))];
  const drivers = [...new Set(routes.filter(r => r.driver).map(r => r.driver!))];

  // Handle URL query params for deep linking from Alerts (route)
  useEffect(() => {
    const routeId = searchParams.get('route');
    if (routeId) {
      // Find route by ID or name
      const route = routes.find(r =>
        r.id === routeId ||
        r.id.toLowerCase().includes(routeId.toLowerCase()) ||
        routeId.toLowerCase().includes(r.id.toLowerCase()) ||
        r.name.toLowerCase().includes(routeId.toLowerCase())
      );
      if (route) {
        cameFromAlerts.current = true;
        setRouteDetailsRoute(route);
      }
      // Clear the query param after handling
      setSearchParams({});
    }
  }, [searchParams, setSearchParams, routes]);

  // Handle closing route detail modal - return to Alerts if came from there
  const handleCloseRouteDetail = () => {
    setRouteDetailsRoute(null);
    if (cameFromAlerts.current) {
      cameFromAlerts.current = false;
      navigate('/alerts');
    }
  };

  const filteredRoutes = routes.filter((route) => {
    // Status filter
    if (filterStatus && route.status !== filterStatus) return false;

    // Vehicle type filter
    if (advancedFilters.vehicleType && route.vehicleType !== advancedFilters.vehicleType) return false;

    // Efficiency range filter
    if (route.efficiency < advancedFilters.minEfficiency || route.efficiency > advancedFilters.maxEfficiency) return false;

    // Distance range filter
    if (route.distance < advancedFilters.minDistance || route.distance > advancedFilters.maxDistance) return false;

    // Driver filter
    if (advancedFilters.driver && route.driver !== advancedFilters.driver) return false;

    return true;
  });

  const clearAdvancedFilters = () => {
    setAdvancedFilters({
      vehicleType: '',
      minEfficiency: 0,
      maxEfficiency: 100,
      minDistance: 0,
      maxDistance: 2000,
      driver: '',
    });
  };

  const hasActiveAdvancedFilters = advancedFilters.vehicleType !== '' ||
    advancedFilters.minEfficiency > 0 ||
    advancedFilters.maxEfficiency < 100 ||
    advancedFilters.minDistance > 0 ||
    advancedFilters.maxDistance < 2000 ||
    advancedFilters.driver !== '';

  const handleOptimize = (route: Route) => {
    setSelectedRoute(route);
    setIsOptimizing(true);
    setOptimizationResult(null);

    // Simulate optimization process
    setTimeout(() => {
      setIsOptimizing(false);
      setOptimizationResult(mockOptimizationResult);
    }, 2000);
  };

  const handleApplyOptimization = async () => {
    if (!selectedRoute || !optimizationResult) return;

    const newEfficiency = Math.min(95, selectedRoute.efficiency + Math.round((optimizationResult.distanceSaved / selectedRoute.distance) * 100));
    const newCost = selectedRoute.estimatedCost - optimizationResult.costSavings;
    const newFuel = selectedRoute.fuelConsumption - optimizationResult.fuelSaved;
    const newCo2 = selectedRoute.co2Emissions - optimizationResult.co2Reduced;

    try {
      // Persist to backend
      await routesAPI.update(selectedRoute.id, {
        status: 'optimized',
        distance: optimizationResult.optimizedDistance,
        duration: optimizationResult.optimizedDuration,
        efficiency: newEfficiency,
        estimated_cost: newCost,
        fuel_consumption: newFuel,
        co2_emissions: newCo2,
      });

      // Update local state on success
      setRoutes(prevRoutes =>
        prevRoutes.map(route => {
          if (route.id === selectedRoute.id) {
            return {
              ...route,
              status: 'optimized' as const,
              distance: optimizationResult.optimizedDistance,
              duration: optimizationResult.optimizedDuration,
              estimatedCost: newCost,
              fuelConsumption: newFuel,
              co2Emissions: newCo2,
              efficiency: newEfficiency,
            };
          }
          return route;
        })
      );
    } catch (error) {
      console.error('Error applying optimization:', error);
    }

    // Close the modal
    setSelectedRoute(null);
    setOptimizationResult(null);
  };

  const getStatusBadge = (status: Route['status']) => {
    const styles = {
      optimized: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      needs_optimization: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      in_progress: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      scheduled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    };
    const labels = {
      optimized: 'Optimized',
      needs_optimization: 'Needs Optimization',
      in_progress: 'In Progress',
      scheduled: 'Scheduled',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const getEfficiencyColor = (efficiency: number) => {
    if (efficiency >= 90) return 'text-green-600 dark:text-green-400';
    if (efficiency >= 80) return 'text-blue-600 dark:text-blue-400';
    if (efficiency >= 70) return 'text-yellow-600 dark:text-yellow-400';
    return 'text-red-600 dark:text-red-400';
  };

  const stats = {
    totalRoutes: routes.length,
    optimized: routes.filter((r) => r.status === 'optimized').length,
    needsOptimization: routes.filter((r) => r.status === 'needs_optimization').length,
    avgEfficiency: Math.round(routes.reduce((acc, r) => acc + r.efficiency, 0) / routes.length),
    totalDistance: routes.reduce((acc, r) => acc + r.distance, 0),
    totalCost: routes.reduce((acc, r) => acc + r.estimatedCost, 0),
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Route Optimization</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Optimize delivery routes for efficiency and cost savings
          </p>
        </div>
        <div className="flex gap-2">
          <button className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
            Export Routes
          </button>
          <button className="inline-flex items-center px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors">
            <PlayIcon className="w-4 h-4 mr-2" />
            Run Batch Optimization
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 cursor-pointer transition hover:shadow-md ${filterStatus === '' ? 'ring-2 ring-teal-500' : ''}`}
          onClick={() => setFilterStatus('')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Routes</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalRoutes}</p>
            </div>
            <MapIcon className="w-8 h-8 text-teal-500" />
          </div>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 cursor-pointer transition hover:shadow-md ${filterStatus === 'optimized' ? 'ring-2 ring-green-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'optimized' ? '' : 'optimized')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Optimized</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.optimized}</p>
            </div>
            <CheckCircleIcon className="w-8 h-8 text-green-500" />
          </div>
        </div>
        <div
          className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4 cursor-pointer transition hover:shadow-md ${filterStatus === 'needs_optimization' ? 'ring-2 ring-yellow-500' : ''}`}
          onClick={() => setFilterStatus(filterStatus === 'needs_optimization' ? '' : 'needs_optimization')}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Needs Review</p>
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.needsOptimization}</p>
            </div>
            <ExclamationTriangleIcon className="w-8 h-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Avg Efficiency</p>
              <p className={`text-2xl font-bold ${getEfficiencyColor(stats.avgEfficiency)}`}>{stats.avgEfficiency}%</p>
            </div>
            <ChartBarIcon className="w-8 h-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Total Distance</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalDistance.toLocaleString()} mi</p>
            </div>
            <TruckIcon className="w-8 h-8 text-gray-500" />
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Est. Cost</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">${stats.totalCost.toLocaleString()}</p>
            </div>
            <CurrencyDollarIcon className="w-8 h-8 text-green-500" />
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="flex gap-2">
            <button
              onClick={() => setFilterStatus('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === ''
                  ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              All Routes
            </button>
            <button
              onClick={() => setFilterStatus('needs_optimization')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === 'needs_optimization'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Needs Optimization
            </button>
            <button
              onClick={() => setFilterStatus('optimized')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filterStatus === 'optimized'
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Optimized
            </button>
          </div>
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className={`inline-flex items-center px-4 py-2 border rounded-lg transition-colors ${
              showAdvancedFilters || hasActiveAdvancedFilters
                ? 'border-teal-500 text-teal-700 dark:text-teal-400 bg-teal-50 dark:bg-teal-900/20'
                : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
          >
            <AdjustmentsHorizontalIcon className="w-4 h-4 mr-2" />
            Advanced Filters
            {hasActiveAdvancedFilters && (
              <span className="ml-2 w-2 h-2 bg-teal-500 rounded-full"></span>
            )}
          </button>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Vehicle Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Vehicle Type
                </label>
                <select
                  value={advancedFilters.vehicleType}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, vehicleType: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">All Vehicle Types</option>
                  {vehicleTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Driver */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Driver
                </label>
                <select
                  value={advancedFilters.driver}
                  onChange={(e) => setAdvancedFilters({ ...advancedFilters, driver: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">All Drivers</option>
                  {drivers.map((driver) => (
                    <option key={driver} value={driver}>{driver}</option>
                  ))}
                </select>
              </div>

              {/* Efficiency Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Efficiency Range (%)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={advancedFilters.minEfficiency}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, minEfficiency: Number(e.target.value) })}
                    className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Min"
                  />
                  <span className="text-gray-500 dark:text-gray-400">to</span>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={advancedFilters.maxEfficiency}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, maxEfficiency: Number(e.target.value) })}
                    className="w-20 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Max"
                  />
                </div>
              </div>

              {/* Distance Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Distance Range (mi)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min="0"
                    value={advancedFilters.minDistance}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, minDistance: Number(e.target.value) })}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Min"
                  />
                  <span className="text-gray-500 dark:text-gray-400">to</span>
                  <input
                    type="number"
                    min="0"
                    value={advancedFilters.maxDistance}
                    onChange={(e) => setAdvancedFilters({ ...advancedFilters, maxDistance: Number(e.target.value) })}
                    className="w-24 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                    placeholder="Max"
                  />
                </div>
              </div>
            </div>

            {/* Filter Actions */}
            <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Showing {filteredRoutes.length} of {routes.length} routes
              </p>
              <div className="flex gap-2">
                <button
                  onClick={clearAdvancedFilters}
                  className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                  Clear Filters
                </button>
                <button
                  onClick={() => setShowAdvancedFilters(false)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition-colors"
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Routes Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredRoutes.map((route) => (
          <div
            key={route.id}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Clickable main content area */}
            <div
              className="p-6 pb-4 cursor-pointer"
              onClick={() => setRouteDetailsRoute(route)}
            >
              <div className="flex justify-between items-start mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white hover:text-teal-600 dark:hover:text-teal-400 transition-colors">{route.name}</h3>
                    {getStatusBadge(route.status)}
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{route.id} • {route.scheduledDate}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${getEfficiencyColor(route.efficiency)}`}>{route.efficiency}%</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Efficiency</p>
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4 text-sm">
                <span className="text-gray-900 dark:text-white font-medium">{route.origin}</span>
                <span className="text-gray-400">→</span>
                <span className="flex items-center text-gray-500 dark:text-gray-400">
                  {route.stops} stops
                </span>
                <span className="text-gray-400">→</span>
                <span className="text-gray-900 dark:text-white font-medium">{route.destination}</span>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Distance</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{route.distance} mi</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{route.duration}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Est. Cost</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">${route.estimatedCost.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Fuel</p>
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">{route.fuelConsumption} gal</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {route.waypoints.slice(0, 4).map((waypoint, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 dark:bg-gray-700 rounded text-xs text-gray-600 dark:text-gray-300"
                  >
                    {waypoint}
                  </span>
                ))}
                {route.waypoints.length > 4 && (
                  <span className="px-2 py-1 bg-teal-100 dark:bg-teal-900/30 rounded text-xs text-teal-700 dark:text-teal-400 font-medium">
                    +{route.waypoints.length - 4} more
                  </span>
                )}
              </div>
            </div>

            {/* Footer with actions - not part of clickable area */}
            <div className="px-6 pb-6">
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                  <span className="flex items-center">
                    <TruckIcon className="w-4 h-4 mr-1" />
                    {route.vehicleType}
                  </span>
                  {route.driver && (
                    <span className="flex items-center">
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-1"></span>
                      {route.driver}
                    </span>
                  )}
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOptimize(route);
                  }}
                  disabled={route.status === 'optimized' || route.status === 'in_progress'}
                  className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    route.status === 'optimized' || route.status === 'in_progress'
                      ? 'bg-gray-100 text-gray-400 dark:bg-gray-700 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400 hover:bg-teal-200 dark:hover:bg-teal-900/50'
                  }`}
                >
                  <ArrowPathIcon className="w-4 h-4 mr-1" />
                  {route.status === 'optimized' ? 'Optimized' : 'Optimize'}
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Optimization Modal */}
      {selectedRoute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    Route Optimization
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">{selectedRoute.name}</p>
                </div>
                <button
                  onClick={() => {
                    setSelectedRoute(null);
                    setOptimizationResult(null);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="p-6">
              {isOptimizing ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-16 w-16 border-4 border-teal-500 border-t-transparent mb-4"></div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Optimizing Route...
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 text-center">
                    Analyzing traffic patterns, road conditions, and delivery windows
                  </p>
                </div>
              ) : optimizationResult ? (
                <div className="space-y-6">
                  <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
                    <div className="flex items-center mb-4">
                      <CheckCircleIcon className="w-8 h-8 text-green-500 mr-3" />
                      <div>
                        <h3 className="text-lg font-semibold text-green-800 dark:text-green-300">
                          Optimization Complete!
                        </h3>
                        <p className="text-green-600 dark:text-green-400 text-sm">
                          Found a more efficient route with significant savings
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Distance Saved</p>
                      <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                        {optimizationResult.distanceSaved} mi
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {optimizationResult.originalDistance} → {optimizationResult.optimizedDistance} mi
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Time Saved</p>
                      <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {optimizationResult.timeSaved}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {optimizationResult.originalDuration} → {optimizationResult.optimizedDuration}
                      </p>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-center">
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">Cost Savings</p>
                      <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                        ${optimizationResult.costSavings}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">per trip</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <div className="w-10 h-10 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Fuel Saved</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{optimizationResult.fuelSaved} gallons</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-xl">
                      <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">CO2 Reduced</p>
                        <p className="font-semibold text-gray-900 dark:text-white">{optimizationResult.co2Reduced} kg</p>
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                    <h4 className="font-medium text-gray-900 dark:text-white mb-3">Optimized Route Order</h4>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="px-3 py-1 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-lg text-sm font-medium">
                        {selectedRoute.origin}
                      </span>
                      {selectedRoute.waypoints.map((waypoint, index) => (
                        <React.Fragment key={index}>
                          <span className="text-gray-400">→</span>
                          <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm">
                            {waypoint}
                          </span>
                        </React.Fragment>
                      ))}
                      <span className="text-gray-400">→</span>
                      <span className="px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium">
                        {selectedRoute.destination}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Click optimize to analyze this route
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
              <button
                onClick={() => {
                  setSelectedRoute(null);
                  setOptimizationResult(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
              {optimizationResult && (
                <button
                  onClick={handleApplyOptimization}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  Apply Optimized Route
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Route Details Modal */}
      {routeDetailsRoute && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex justify-between items-start">
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                    {routeDetailsRoute.name}
                  </h2>
                  <p className="text-gray-500 dark:text-gray-400">
                    {routeDetailsRoute.id} • {routeDetailsRoute.origin} → {routeDetailsRoute.destination}
                  </p>
                </div>
                <button
                  onClick={handleCloseRouteDetail}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Route Map */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <MapIcon className="w-4 h-4 mr-2" />
                  Route Map
                </h3>
                <div className="h-64 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700">
                  <MapContainer
                    center={(() => {
                      // Calculate center from all route points
                      const allPoints = [routeDetailsRoute.origin, ...routeDetailsRoute.waypoints, routeDetailsRoute.destination];
                      const coords = allPoints.map(p => cityCoordinates[p]).filter(Boolean);
                      if (coords.length === 0) return [39.8283, -98.5795] as [number, number];
                      const avgLat = coords.reduce((sum, c) => sum + c[0], 0) / coords.length;
                      const avgLng = coords.reduce((sum, c) => sum + c[1], 0) / coords.length;
                      return [avgLat, avgLng] as [number, number];
                    })()}
                    zoom={5}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />

                    {/* Route polyline */}
                    {(() => {
                      const allPoints = [routeDetailsRoute.origin, ...routeDetailsRoute.waypoints, routeDetailsRoute.destination];
                      const coords = allPoints.map(p => cityCoordinates[p]).filter(Boolean);
                      return coords.length > 1 ? (
                        <Polyline
                          positions={coords}
                          pathOptions={{ color: '#14b8a6', weight: 3, dashArray: '10, 5' }}
                        />
                      ) : null;
                    })()}

                    {/* Origin marker */}
                    {cityCoordinates[routeDetailsRoute.origin] && (
                      <Marker position={cityCoordinates[routeDetailsRoute.origin]}>
                        <Popup>
                          <strong>Origin:</strong> {routeDetailsRoute.origin}
                        </Popup>
                      </Marker>
                    )}

                    {/* Waypoint markers */}
                    {routeDetailsRoute.waypoints.map((waypoint, index) => {
                      const coords = cityCoordinates[waypoint];
                      if (!coords) return null;
                      return (
                        <Marker key={index} position={coords}>
                          <Popup>
                            <strong>Stop {index + 1}:</strong> {waypoint}
                          </Popup>
                        </Marker>
                      );
                    })}

                    {/* Destination marker */}
                    {cityCoordinates[routeDetailsRoute.destination] && routeDetailsRoute.destination !== routeDetailsRoute.origin && (
                      <Marker position={cityCoordinates[routeDetailsRoute.destination]}>
                        <Popup>
                          <strong>Destination:</strong> {routeDetailsRoute.destination}
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
              </div>

              {/* All Waypoints */}
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center">
                  <MapPinIcon className="w-4 h-4 mr-2" />
                  Complete Route ({routeDetailsRoute.waypoints.length + 2} stops)
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-3 py-1.5 bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 rounded-lg text-sm font-medium flex items-center">
                    <span className="w-5 h-5 bg-teal-500 text-white rounded-full flex items-center justify-center text-xs mr-2">S</span>
                    {routeDetailsRoute.origin}
                  </span>
                  {routeDetailsRoute.waypoints.map((waypoint, index) => (
                    <React.Fragment key={index}>
                      <span className="text-gray-400">→</span>
                      <span className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm flex items-center">
                        <span className="w-5 h-5 bg-gray-400 text-white rounded-full flex items-center justify-center text-xs mr-2">{index + 1}</span>
                        {waypoint}
                      </span>
                    </React.Fragment>
                  ))}
                  <span className="text-gray-400">→</span>
                  <span className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg text-sm font-medium flex items-center">
                    <span className="w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs mr-2">E</span>
                    {routeDetailsRoute.destination}
                  </span>
                </div>
              </div>

              {/* Route Stats */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Route Details</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Distance</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{routeDetailsRoute.distance} mi</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Duration</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{routeDetailsRoute.duration}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Est. Cost</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">${routeDetailsRoute.estimatedCost.toLocaleString()}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                    <p className="text-xs text-gray-500 dark:text-gray-400">Fuel</p>
                    <p className="text-lg font-semibold text-gray-900 dark:text-white">{routeDetailsRoute.fuelConsumption} gal</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={handleCloseRouteDetail}
                className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RouteOptimization;
